// components/DashboardHome.js - С оптимизацией загрузки и статистикой доходов

import { useState, useEffect } from 'react';
import { TrendingUp, ArrowRight, ChevronLeft, ChevronRight, User, Users, Wallet,Copy, Share2, Check, UserPlus, Award, Target, Calendar, MessageCircle, Bell, ExternalLink, Clock, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { authService } from '../services/authService';
import { dashboardService } from '../services/dashboardService';
import TelegramIntegration from './TelegramIntegration';
import { useAuth } from '../contexts/AuthContext';

const DashboardHome = () => {
    const { user: authUser, loading: authLoading } = useAuth();
    const [userData, setUserData] = useState(null);
    const [referralLink, setReferralLink] = useState('');
    const [copied, setCopied] = useState(false);
    const [dashboardStats, setDashboardStats] = useState({
        totalIncome: {
            lastMonthUsd: 0,
            totalUsd: 0,
            growth: 0
        },
        partnersInfo: {
            total: 0,
            active: 0,
            totalTeam: null, // null = загружается
            recentPartners: []
        }
    });
    const [isLoading, setIsLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(false);
    const [incomeLoading, setIncomeLoading] = useState(false); // НОВОЕ: для статистики доходов
    const [currentSlide, setCurrentSlide] = useState(0);

    const usefulLinks = [
        { id: 1, title: 'Чат сообщества', url: 'https://t.me/+EWbDe4ZUdmRlMWJi', icon: 'telegram' },
        { id: 2, title: 'Новостной канал', url: 'https://t.me/+QIkrGiBoYohiNDBi', icon: 'telegram' },
    ];

    const news = [
        {
            id: 1,
            title: 'Запуск нового направления: кластеры здоровья',
            content: 'Мы рады объявить о запуске нового направления в рамках нашей платформы.',
            date: '2025-01-15'
        },
        {
            id: 2,
            title: 'Обновление системы выплат',
            content: 'С 1 февраля вводится новая система автоматических выплат.',
            date: '2025-01-20'
        },
        {
            id: 3,
            title: 'Конкурс "Лучший партнер месяца"',
            content: 'Принимайте участие в ежемесячном конкурсе и выигрывайте призы!',
            date: '2025-01-25'
        }
    ];

    // Быстрая загрузка основных данных
    useEffect(() => {
        if (authUser) {
            console.log('✅ Быстрая загрузка основных данных из AuthContext');

            const teamStats = authUser.team_statistics;

            // Устанавливаем данные пользователя
            setUserData(authUser);
            setReferralLink(authUser.referral_link || '');

            // Устанавливаем базовую статистику (быстро)
            setDashboardStats({
                totalIncome: {
                    lastMonthUsd: authUser.total_earned_usd || 0,
                    totalUsd: authUser.total_earned_usd || 0,
                    growth: authUser.income_growth || 0
                },
                partnersInfo: {
                    total: authUser.partners_count || 0,
                    active: authUser.active_partners_count || 0,
                    totalTeam: null, // Будет загружено отдельно
                    recentPartners: teamStats?.recent_partners || []
                }
            });

            setIsLoading(false);
        }
    }, [authUser]);

    // Загрузка полной статистики команды (медленно, но отдельно)
    useEffect(() => {
        if (authUser && !isLoading) {
            const timer = setTimeout(() => {
                fetchFullTeamStats();
            }, 300); // Задержка для плавности

            return () => clearTimeout(timer);
        }
    }, [authUser, isLoading]);

    // НОВОЕ: Загрузка статистики доходов из FinancialOperation
    useEffect(() => {
        if (authUser && !isLoading) {
            const timer = setTimeout(() => {
                fetchIncomeStats();
            }, 500); // Задержка для плавности загрузки

            return () => clearTimeout(timer);
        }
    }, [authUser, isLoading]);

    const fetchFullTeamStats = async () => {
        try {
            setStatsLoading(true);
            console.log('📄 Загрузка полной статистики команды...');

            const response = await authService.getTeamStats();

            if (response.success) {
                const teamStats = response.data;

                // Обновляем только статистику команды
                setDashboardStats(prev => ({
                    ...prev,
                    partnersInfo: {
                        ...prev.partnersInfo,
                        totalTeam: teamStats.all_team?.total || 0,
                        recentPartners: teamStats.recent_partners || prev.partnersInfo.recentPartners
                    }
                }));

                console.log('✅ Полная статистика команды загружена');
            } else {
                console.error('❌ Ошибка загрузки статистики:', response.message);
                // В случае ошибки показываем базовую статистику
                setDashboardStats(prev => ({
                    ...prev,
                    partnersInfo: {
                        ...prev.partnersInfo,
                        totalTeam: prev.partnersInfo.total // Fallback
                    }
                }));
            }
        } catch (err) {
            console.error('❌ Ошибка загрузки статистики команды:', err);
            // В случае ошибки показываем базовую статистику
            setDashboardStats(prev => ({
                ...prev,
                partnersInfo: {
                    ...prev.partnersInfo,
                    totalTeam: prev.partnersInfo.total // Fallback
                }
            }));
        } finally {
            setStatsLoading(false);
        }
    };

    // НОВАЯ ФУНКЦИЯ: Загрузка реальной статистики доходов
    const fetchIncomeStats = async () => {
        try {
            setIncomeLoading(true);
            console.log('💰 Загрузка статистики доходов из FinancialOperation...');

            const token = localStorage.getItem('auth_token');
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

            const response = await fetch(`${API_URL}/dashboard/stats`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Ошибка загрузки статистики');
            }

            const data = await response.json();

            if (data.success && data.data.income_statistics) {
                const incomeStats = data.data.income_statistics;

                // Обновляем состояние с реальными данными из БД
                setDashboardStats(prev => ({
                    ...prev,
                    totalIncome: {
                        lastMonthRub: incomeStats.last_month_income_rub || 0,
                        totalRub: incomeStats.total_income_rub || 0,
                        lastMonthUsd: incomeStats.last_month_income_usd || 0,
                        totalUsd: incomeStats.total_income_usd || 0,
                        growth: incomeStats.growth_percentage || 0
                    }
                }));

                console.log('✅ Статистика доходов загружена:', {
                    totalRub: incomeStats.total_income_rub,
                    totalUsd: incomeStats.total_income_usd,
                    growth: incomeStats.growth_percentage,
                    operations: incomeStats.total_operations
                });
            }
        } catch (err) {
            console.error('❌ Ошибка загрузки статистики доходов:', err);
            // В случае ошибки оставляем базовые данные из authUser
        } finally {
            setIncomeLoading(false);
        }
    };

    const refreshTeamStats = () => {
        setDashboardStats(prev => ({
            ...prev,
            partnersInfo: {
                ...prev.partnersInfo,
                totalTeam: null // Сброс для показа загрузки
            }
        }));
        fetchFullTeamStats();
    };

    // НОВАЯ ФУНКЦИЯ: Обновление статистики доходов
    const refreshIncomeStats = () => {
        setIncomeLoading(true);
        fetchIncomeStats();
    };

    const copyReferralLink = () => {
        if (referralLink) {
            navigator.clipboard.writeText(referralLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % news.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + news.length) % news.length);
    };

    const formatCurrency = (amount) => {
        if (!amount || amount === 0) {
            return '0.00';
        }

        return amount.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const getIconComponent = (iconName) => {
        switch (iconName) {
            case 'telegram':
                return <MessageCircle size={16} className="text-blue-500" />;
            case 'vk':
                return <Users size={16} className="text-blue-600" />;
            default:
                return <ExternalLink size={16} className="text-gray-500" />;
        }
    };

    if (authLoading || isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Приветствие */}
            <div className="bg-gradient-to-r from-blue-primary to-blue-primary/80 rounded-xl text-white p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold mb-2">
                            Добро пожаловать, {userData?.first_name || userData?.login || 'Партнер'}!
                        </h1>
                        <p className="text-blue-100">
                            Управляйте своим бизнесом и развивайте партнерскую сеть
                        </p>
                    </div>
                    <div className="hidden md:block">
                        <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center">
                            {userData?.avatar ? (
                                <img
                                    src={userData.avatar}
                                    alt="Аватар"
                                    className="w-20 h-20 rounded-full object-cover"
                                />
                            ) : (
                                <User size={32} className="text-white" />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Статистика - 4 карточки */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Общие доходы - КРАСИВАЯ КАРТОЧКА */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl shadow-lg border-2 border-green-200 p-6 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl shadow-md">
                            <TrendingUp className="text-white" size={24} />
                        </div>
                        <div className="flex items-center gap-2">
                            {incomeLoading && (
                                <Clock className="text-green-600 animate-pulse" size={16} />
                            )}
                            {!incomeLoading && dashboardStats.totalIncome.growth !== 0 && (
                                <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                                    dashboardStats.totalIncome.growth > 0
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-red-100 text-red-700'
                                }`}>
                                    {dashboardStats.totalIncome.growth > 0 ? '↑' : '↓'}
                                    {Math.abs(dashboardStats.totalIncome.growth).toFixed(1)}%
                                </span>
                            )}
                            {!incomeLoading && (
                                <button
                                    onClick={refreshIncomeStats}
                                    className="p-1 hover:bg-white/80 rounded-full transition-colors"
                                    title="Обновить статистику"
                                >
                                    <RefreshCw size={14} className="text-gray-600" />
                                </button>
                            )}
                        </div>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">Общие доходы</h3>
                    {incomeLoading ? (
                        <div className="space-y-2">
                            <div className="h-8 w-24 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-shimmer bg-[length:200%_100%]"></div>
                            <div className="h-4 w-20 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-shimmer bg-[length:200%_100%]"></div>
                        </div>
                    ) : (
                        <>
                            <p className="text-3xl font-bold text-gray-900 mb-1">
                                {formatCurrency(dashboardStats.totalIncome.totalUsd)}
                                <span className="text-xl font-semibold text-green-600 ml-2">USDT</span>
                            </p>
                            {dashboardStats.totalIncome.lastMonthUsd > 0 && (
                                <p className="text-xs text-gray-500">
                                    За месяц: {formatCurrency(dashboardStats.totalIncome.lastMonthUsd)} USDT
                                </p>
                            )}
                        </>
                    )}
                </div>

                {/* Прямые партнеры - КРАСИВАЯ КАРТОЧКА */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg border-2 border-blue-200 p-6 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl shadow-md">
                            <UserPlus className="text-white" size={24} />
                        </div>
                        {dashboardStats.partnersInfo.active > 0 && (
                            <div className="flex items-center gap-1 px-2 py-1 bg-white/80 rounded-full">
                                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                <span className="text-xs font-medium text-gray-700">{dashboardStats.partnersInfo.active} активных</span>
                            </div>
                        )}
                    </div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">Прямые партнеры</h3>
                    <p className="text-3xl font-bold text-gray-900 mb-1">
                        {dashboardStats.partnersInfo.total}
                        <span className="text-xl font-semibold text-blue-600 ml-2">чел.</span>
                    </p>
                    <p className="text-xs text-gray-500">
                        Ваши рефералы
                    </p>
                </div>

                {/* Общая команда - КРАСИВАЯ КАРТОЧКА */}
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-lg border-2 border-purple-200 p-6 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-md">
                            <Users className="text-white" size={24} />
                        </div>
                        {statsLoading && dashboardStats.partnersInfo.totalTeam === null && (
                            <Clock className="text-purple-600 animate-pulse" size={16} />
                        )}
                    </div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">Общая команда</h3>
                    {statsLoading && dashboardStats.partnersInfo.totalTeam === null ? (
                        <div className="space-y-2">
                            <div className="h-8 w-20 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-shimmer bg-[length:200%_100%]"></div>
                            <div className="h-4 w-32 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-shimmer bg-[length:200%_100%]"></div>
                        </div>
                    ) : (
                        <>
                            <p className="text-3xl font-bold text-gray-900 mb-1">
                                {dashboardStats.partnersInfo.totalTeam || 0}
                                <span className="text-xl font-semibold text-purple-600 ml-2">чел.</span>
                            </p>
                            <p className="text-xs text-gray-500">
                                Все уровни структуры
                            </p>
                        </>
                    )}
                </div>

                {/* Баланс - КРАСИВАЯ КАРТОЧКА */}
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl shadow-lg border-2 border-emerald-200 p-6 hover:shadow-xl transition-shadow">
                    <div className="flex items-center justify-between mb-4">
                        <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl shadow-md">
                            <Wallet className="text-white" size={24} />
                        </div>
                        <div className="flex items-center gap-1 px-3 py-1 bg-white/80 rounded-full">
                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                            <span className="text-xs font-medium text-gray-700">Доступно</span>
                        </div>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-2">Ваш баланс</h3>
                    <p className="text-3xl font-bold text-gray-900 mb-1">
                        {formatCurrency(userData?.balance_usd || 0)}
                        <span className="text-xl font-semibold text-emerald-600 ml-2">USDT</span>
                    </p>
                    <p className="text-xs text-gray-500">
                        Криптовалюта TRC-20
                    </p>
                </div>
            </div>

            {/* Основной контент */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Левая колонка */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Реферальная ссылка */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <UserPlus className="mr-2" size={20} />
                            Пригласить партнеров
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Поделитесь своей реферальной ссылкой и зарабатывайте с каждого привлеченного партнера
                        </p>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={referralLink}
                                readOnly
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                            />
                            <button
                                onClick={copyReferralLink}
                                className="px-4 py-2 bg-blue-primary text-white rounded-lg hover:bg-blue-primary/90 transition-colors flex items-center gap-2"
                            >
                                {copied ? (
                                    <>
                                        <Check size={16} />
                                        Скопировано
                                    </>
                                ) : (
                                    <>
                                        <Copy size={16} />
                                        Копировать
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Последние партнеры */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-800">Последние партнеры</h3>
                            <Link
                                to="/dashboard/partners"
                                className="text-sm text-blue-primary hover:underline flex items-center"
                            >
                                Все партнеры
                                <ArrowRight size={14} className="ml-1" />
                            </Link>
                        </div>

                        {dashboardStats.partnersInfo.recentPartners.length > 0 ? (
                            <div className="space-y-3">
                                {dashboardStats.partnersInfo.recentPartners.slice(0, 5).map((partner) => (
                                    <div key={partner.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                                        <div className="flex items-center">
                                            {partner.avatar && partner.avatar !== false ? (
                                                <img
                                                    src={`https://api.lite-com.ru/storage/${partner.avatar}`}
                                                    alt={partner.login}
                                                    className="w-8 h-8 rounded-full object-cover mr-3"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div
                                                className={`w-8 h-8 bg-gradient-to-br from-blue-primary to-blue-primary/80 rounded-full flex items-center justify-center mr-3 ${
                                                    partner.avatar && partner.avatar !== false ? 'hidden' : ''
                                                }`}
                                            >
                                                <span className="text-white text-xs font-semibold">
                                                    {partner.full_name?.split(' ').map(n => n[0]).join('') || partner.login?.[0] || 'P'}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">
                                                    {partner.full_name || partner.login}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {partner.registration_date ? new Date(partner.registration_date).toLocaleDateString('ru-RU') : ''}
                                                </p>
                                            </div>
                                        </div>
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                            partner.status === 'active'
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {partner.status === 'active' ? 'Активен' : 'Неактивен'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                                <Users className="mx-auto mb-2 text-gray-400" size={32} />
                                <p className="text-sm">У вас пока нет партнеров</p>
                                <p className="text-xs">Поделитесь реферальной ссылкой</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Правая колонка */}
                <div className="space-y-6">
                    {/* Telegram интеграция */}
                    <TelegramIntegration user={userData} isProfilePage={false} />

                    {/* Полезные ссылки */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Полезные ссылки</h3>
                        <div className="space-y-2">
                            {usefulLinks.map((link) => (
                                <a
                                    key={link.id}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                                >
                                    {getIconComponent(link.icon)}
                                    <span className="ml-2 text-sm text-gray-700 group-hover:text-gray-900 flex-1">
                                        {link.title}
                                    </span>
                                    <ExternalLink size={14} className="text-gray-400 group-hover:text-gray-600" />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Новости */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                                <Bell className="mr-2" size={20} />
                                Новости
                            </h3>
                            <div className="flex gap-1">
                                <button
                                    onClick={prevSlide}
                                    className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                                >
                                    <ChevronLeft size={16} className="text-gray-600" />
                                </button>
                                <button
                                    onClick={nextSlide}
                                    className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                                >
                                    <ChevronRight size={16} className="text-gray-600" />
                                </button>
                            </div>
                        </div>

                        <div className="relative">
                            {news.length > 0 && (
                                <div className="space-y-3">
                                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                                        <h4 className="font-semibold text-gray-800 mb-2 text-sm">
                                            {news[currentSlide].title}
                                        </h4>
                                        <p className="text-xs text-gray-600 mb-2">
                                            {news[currentSlide].content}
                                        </p>
                                        <div className="flex items-center text-xs text-gray-500">
                                            <Calendar size={12} className="mr-1" />
                                            {new Date(news[currentSlide].date).toLocaleDateString('ru-RU')}
                                        </div>
                                    </div>

                                    {/* Индикаторы слайдов */}
                                    <div className="flex justify-center space-x-1">
                                        {news.map((_, index) => (
                                            <button
                                                key={index}
                                                onClick={() => setCurrentSlide(index)}
                                                className={`w-2 h-2 rounded-full transition-colors ${
                                                    index === currentSlide
                                                        ? 'bg-blue-primary'
                                                        : 'bg-gray-300 hover:bg-gray-400'
                                                }`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Быстрые действия */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Быстрые действия</h3>

                        <div className="space-y-3">
                            <Link
                                to="/dashboard/profile"
                                className="flex items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                            >
                                <User className="text-gray-500 mr-3" size={16} />
                                <span className="text-gray-700 group-hover:text-gray-900 transition-colors">
                                    Редактировать профиль
                                </span>
                                <ArrowRight className="ml-auto text-gray-400 group-hover:text-gray-600 transition-colors" size={14} />
                            </Link>

                            <Link
                                to="/dashboard/partners"
                                className="flex items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                            >
                                <Users className="text-gray-500 mr-3" size={16} />
                                <span className="text-gray-700 group-hover:text-gray-900 transition-colors">
                                    Управление командой
                                </span>
                                <ArrowRight className="ml-auto text-gray-400 group-hover:text-gray-600 transition-colors" size={14} />
                            </Link>

                            <div className="flex items-center p-3 bg-gray-50 rounded-lg opacity-50">
                                <Target className="text-gray-400 mr-3" size={16} />
                                <span className="text-gray-500">
                                    Финансы и выплаты
                                </span>
                                <span className="ml-auto text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                                    Скоро
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardHome;
